import { datum, type Datum } from './datum';

/**
 * TLS ClientHello signature lookup.
 *
 * IMPORTANT — why this is not JA4:
 * JA4 hashes the *ordered* cipher and extension lists from the raw ClientHello.
 * A static page cannot see a ClientHello, and Cloudflare's free tier exposes
 * only SHA-1 hashes of those lists (tlsClientCiphersSha1 / tlsClientExtensionsSha1),
 * not the lists themselves. So this table maps a (cipher-hash, extension-hash)
 * pair to a known client, which is strictly weaker than JA4: it discards
 * ordering, and Chromium's extension reordering defeats it.
 *
 * True JA4/JA3 are only available under request.cf.botManagement (a paid
 * Cloudflare Bot Management add-on). The edge probe reads that field when
 * present, and the UI prefers it over this lookup.
 *
 * The table is intentionally empty. A match would be meaningful, but a miss is
 * NOT evidence of automation, and we have no verified captures of the exact
 * SHA-1 pairs Cloudflare emits (it does not document the hash construction).
 * Guessing values here would manufacture false confidence, so the table ships
 * empty and the UI says "unmapped" honestly.
 *
 * FoxIO's own docs note JA4 fingerprints change roughly yearly as TLS libraries
 * update, so any row added here must be treated as needing periodic revalidation.
 */

export interface TlsSignature {
  label: string;
  confidence: Datum<string>['confidence'];
  note?: string;
}

export interface TlsSignatureRow {
  /** `${tlsClientCiphersSha1}:${tlsClientExtensionsSha1}` */
  key: string;
  label: string;
  confidence: Datum<string>['confidence'];
  source: string;
  note?: string;
}

/**
 * Known signatures. Empty until a capture is verified against a citable source.
 * See the module comment for why guessing is worse than an honest miss.
 */
export const TLS_SIGNATURES: TlsSignatureRow[] = [];

/**
 * Lookup by the pair of ClientHello hashes.
 *
 * Returns null on a miss, which the UI must present as "unknown / unmapped" —
 * never as a bot signal.
 */
export function lookupTlsSignature(
  ciphersSha1: string | undefined,
  extensionsSha1: string | undefined,
): Datum<TlsSignature> | null {
  if (!ciphersSha1 || !extensionsSha1) return null;
  const row = TLS_SIGNATURES.find((r) => r.key === `${ciphersSha1}:${extensionsSha1}`);
  if (!row) return null;
  return datum(
    { label: row.label, confidence: row.confidence, note: row.note },
    row.source,
    row.confidence,
    { note: row.note },
  );
}
